import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePaginatedQuery } from '../../../hooks/use-paginated-query';
import type { CompanyListQuery, CompanyRequest } from '../types/api';
import type { Company } from '../types/company';
import { useCompaniesApi } from './use-companies-api';

const QK_SEARCH_COMPANIES = (query: CompanyListQuery) => ['companies', query] as const;
const QK_GET_COMPANY = (id: string) => ['company', id] as const;

/** Paginated, searchable list of companies. */
export function useSearchCompanies(query: CompanyListQuery) {
  const { companiesApi } = useCompaniesApi();

  return usePaginatedQuery<Company, CompanyListQuery>(query, {
    queryKey: QK_SEARCH_COMPANIES(query),
    queryFn: (args, paginationOpts) => companiesApi.list(args, paginationOpts),
  });
}

/** Fetch a single company (by SparkLayer id) including its relations. */
export function useCompany(id: string | undefined) {
  const { companiesApi } = useCompaniesApi();

  return useQuery({
    queryKey: QK_GET_COMPANY(id ?? ''),
    queryFn: () => companiesApi.company({ by: 'sparklayer', identifier: id ?? '' }).get(),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const { companiesApi } = useCompaniesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CompanyRequest) => companiesApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useUpdateCompany(id: string) {
  const { companiesApi } = useCompaniesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CompanyRequest) =>
      companiesApi.company({ by: 'sparklayer', identifier: id }).update(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      void queryClient.invalidateQueries({ queryKey: QK_GET_COMPANY(id) });
    },
  });
}

export function useDeleteCompany() {
  const { companiesApi } = useCompaniesApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => companiesApi.company({ by: 'sparklayer', identifier: id }).delete(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}
